import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import type { Contact, Group } from '../../../ContactsAndGroups/ContactsAndGroupsContext'
import type {
  AlertTypeId,
  AlertWithPossibleId,
  Rule,
  V3AlertOnly,
  WeatherForecastAlertOnly,
} from '../../_types'
import type { SleepUnitsType } from '../ModalDrawerCreateOrEditAlert'

const alertIsV3 = (alert: AlertWithPossibleId | null): alert is V3AlertOnly | null => {
  if (alert === null) return true

  return alert.type.id !== 'weatherForecastAlert'
}

const alertIsWeatherForecast = (
  alert: AlertWithPossibleId | null,
): alert is WeatherForecastAlertOnly | null => {
  if (alert === null) return true

  return alert.type.id === 'weatherForecastAlert'
}

export const convertAPIAlertToInitivalValues = ({
  initialValues,
  allGroups,
}: {
  initialValues: AlertWithPossibleId | null
  allGroups: Group[]
}):
  | {
      alertTypeId: 'weatherForecastAlert'
      contacts: Contact[]
      coverageBlocks: string[]
      coverageProperties: number[]
      coverageStations: string[]
      coverageType: string
      endDate: moment.Moment | null
      groups: Group[]
      hourOfDay: string
      insectId: null
      name: string | null
      notifyOwnerByEmail: boolean
      notifyOwnerBySMS: boolean
      rules: WeatherForecastAlertOnly['rules'] | null
      startDate: moment.Moment | null
    }
  | {
      alertTypeId: AlertTypeId | null
      contacts: Contact[]
      coverageProperties: number[]
      coverageStations: string[]
      coverageBlocks: string[]
      coverageType: string
      endDate: moment.Moment | null
      frequencyMinutes: number | null
      groups: Group[]
      insectId: number | null
      name: string | null
      notifyOwnerByEmail: boolean
      notifyOwnerBySMS: boolean
      rules: Rule[]
      sleepMinutes: number | null
      sleepUnits: SleepUnitsType
      startDate: moment.Moment | null
      hourOfDay: null
    } => {
  const propertiesObject = fieldAssetStore.getState().properties
  // note that blocks and repeatAnnually are not included, as they weren't used at the time of coding this
  const alertTypeId = (initialValues && initialValues.type.id) || null

  const contacts: Contact[] =
    (initialValues && initialValues.contacts && initialValues.contacts.length && initialValues.contacts) || []

  const groups: Group[] =
    (initialValues &&
      initialValues.groups &&
      initialValues.groups.length &&
      initialValues.groups
        .map((group) => {
          const groupInAllGroups = allGroups.find((g) => g.id === group.id)

          if (groupInAllGroups)
            return { ...groupInAllGroups, alertsContactMethods: group.alertsContactMethods }

          return null
        })
        .filter((g) => g !== null)) ||
    []

  const endDate =
    (initialValues && initialValues.endDate && moment.tz(initialValues.endDate, 'America/Los_Angeles')) ||
    null

  const name = (initialValues && initialValues.name) || null
  const alertIsV3Alert = alertIsV3(initialValues)

  const notifyOwnerByEmail =
    initialValues && typeof initialValues.notifyOwnerByEmail === 'boolean'
      ? !!initialValues.notifyOwnerByEmail
      : true // should be true by default

  const notifyOwnerBySMS =
    initialValues && typeof initialValues.notifyOwnerBySMS === 'boolean'
      ? !!initialValues.notifyOwnerBySMS
      : !!userDetailsStore.getState().smsNumber // make it true if the user has one

  const startDate =
    (initialValues && initialValues.startDate && moment.tz(initialValues.startDate, 'America/Los_Angeles')) ||
    null

  let coverageType = 'all'
  let coverageProperties: number[] = !initialValues ? [] : initialValues.properties.map((p) => p.id)

  coverageProperties = [...new Set(coverageProperties)]

  if (coverageProperties.length) coverageType = 'properties'

  let coverageStations: string[] = []
  let coverageBlocks: string[] = []

  const hasStations = initialValues && Array.isArray(initialValues.stations) && initialValues.stations.length
  const hasBlocks = initialValues && Array.isArray(initialValues.blocks) && initialValues.blocks.length

  if (initialValues && (hasStations || hasBlocks)) {
    coverageProperties = []

    coverageType = 'stationsOrBlocks'

    /**
     * we want to build an array of stations if the coverage was for specific stations,
     * but all we have is the geom for a station, and propertyIds, but we need to have
     * strings of the form `${propertyId}-${stationGEOM}`, so we can map over the stations
     * and add each property id in front of them, but do it cleverly so that we only
     * include property/geom pairs that exist in reality. Also, make it a unique array,
     * to avoid exceptions—though there SHOULDN'T be any
     */
    if (hasStations) {
      coverageStations = [
        ...new Set(
          initialValues.stations
            .map((sId) => {
              // for each station, map over each property that we received
              return (initialValues.properties || [])
                .map((pId) => {
                  // make a string of propertyId-GEOM, but only if that property/geom combo exist in reality
                  try {
                    const property = propertiesObject && propertiesObject[Number(pId.id)]

                    const isPointInPropertyHasThatGeom = Object.values(property?.points || {}).some(
                      (p) => p.geom === sId.geom,
                    )

                    if (isPointInPropertyHasThatGeom) return `${pId.id}&${sId.geom}`
                  } catch (err) {}

                  return ''
                })
                .filter((s) => !!s.length) // filter out the empty strings from the mapping
            })
            // do a reduce to flatten the array
            .reduce((a, b) => a.concat(b), []),
        ),
      ]
    }

    if (hasBlocks) {
      coverageBlocks = [
        ...new Set(
          initialValues.blocks
            .map((bId) => {
              // for each station, map over each property that we received
              return (initialValues.properties || [])
                .map((pId) => {
                  // make a string of propertyId-blockId, but only if that property/blockId combo exist in reality
                  try {
                    const property = propertiesObject && propertiesObject[Number(pId.id)]

                    if (property?.blocks && property.blocks[Number(bId?.id)]) {
                      return `${pId.id}&${bId.id}`
                    }
                  } catch (err) {}

                  // otherwise return empty string
                  return ''
                })
                .filter((s) => !!s.length) // filter out the empty strings from the mapping
            })
            // do a reduce to flatten the array
            .reduce((a, b) => a.concat(b), []),
        ),
      ]
    }
  }

  if (!alertIsV3Alert) {
    const alert = alertIsWeatherForecast(initialValues) ? initialValues : null

    return {
      alertTypeId: 'weatherForecastAlert',
      contacts,
      coverageBlocks,
      coverageProperties,
      coverageStations,
      coverageType,
      endDate,
      groups,
      hourOfDay: alert?.hourOfDay ?? '06',
      insectId: null,
      name,
      notifyOwnerByEmail,
      notifyOwnerBySMS,
      rules: alert?.rules ?? null,
      startDate,
    }
  }

  const frequencyMinutes = (initialValues && initialValues.frequencyMinutes) || null
  const insectId = (initialValues && Number(initialValues.insect?.id)) || null
  const rules = (initialValues && Array.isArray(initialValues.rules) && initialValues.rules) || []
  const sleepMinutes = (initialValues && initialValues.sleepMinutes) || null

  let sleepUnits: SleepUnitsType = 'minutes'

  if (sleepMinutes) {
    if (sleepMinutes % 1440 === 0) sleepUnits = 'days'
    else if (sleepMinutes % 60 === 0) sleepUnits = 'hours'
  }

  return {
    alertTypeId,
    contacts,
    coverageProperties,
    coverageStations,
    coverageBlocks,
    coverageType,
    endDate,
    frequencyMinutes,
    groups,
    insectId,
    name,
    notifyOwnerByEmail,
    notifyOwnerBySMS,
    rules,
    sleepMinutes,
    sleepUnits,
    startDate,
    hourOfDay: null,
  }
}
